import React, { useState, useEffect, useRef } from 'react';
import { Music2, Headphones, ChevronLeft, ChevronRight, Clock, Play, Pause } from 'lucide-react';
import { STYLE_PREVIEWS, StyleWaveform } from './StylePreview';
import { API_BASE as API } from '../../lib/apiBase';
import '../../styles/producer.css';

const CorcheaIcon = ({ size = 14, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M12 3a1 1 0 011 1v10.27A4 4 0 1111 18.13V4a1 1 0 011-1zm0 17a2 2 0 100-4 2 2 0 000 4z" />
    <path d="M13 4h5v3h-5V4z" />
  </svg>
);

const STYLE_PADS = [
  { id: 'trap', label: 'TRAP', color: '#7000FF', msg: 'Hazlo trap, 808s profundos, hi-hats rápidos, oscuro y con actitud', 
    desc: '808 profundo · Hi-hats rápidos', bpm: '140-150 BPM' },
  { id: 'reggaeton', label: 'REGGAETÓN', color: '#FF6B00', msg: 'Dembow clásico, reggaetón con flow latino y pegadizo',
    desc: 'Dembow clásico · Flow latino', bpm: '90-100 BPM' },
  { id: 'electronic', label: 'ELECTRO', color: '#00D4FF', msg: 'Electrónica moderna, synths fuertes, drop potente y épica',
    desc: 'Synths épicos · Drop potente', bpm: '125-130 BPM' },
  { id: 'pop', label: 'POP', color: '#FF0066', msg: 'Un pop hit con gancho fuerte, producción limpia, que pegue',
    desc: 'Gancho pegadizo · Radio-ready', bpm: '100-120 BPM' },
  { id: 'rnb', label: 'R&B', color: '#F5C842', msg: 'R&B con soul, groovy, muy melódico y emotivo',
    desc: 'Soul moderno · Groovy', bpm: '70-90 BPM' },
  { id: 'surprise', label: '???', color: '#00FF88', msg: 'Sorpréndeme, hazlo único, algo que nadie haya escuchado',
    desc: 'Experimental · Único', bpm: 'Variable' },
];

export default function ProducerEntryScreen({ onCreateMusic, onImproveMusic, onResumeSession, balance = 0, token, projects = [], onNewProject }) {
  const [view, setView] = useState('main');
  const [selectedPad, setSelectedPad] = useState(null);
  const [hoveredPad, setHoveredPad] = useState(null);
  const [previewPlayingPad, setPreviewPlayingPad] = useState(null);
  const [sessions, setSessions] = useState([]);
  const previewAudioRef = useRef(null);

  // Use projects from parent or fetch fresh
  useEffect(() => {
    if (projects && projects.length > 0) {
      setSessions(projects.slice(0, 6));
    } else if (token) {
      fetch(`${API}/api/producer/sessions`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : { sessions: [] })
        .then(data => setSessions((data.sessions || []).slice(0, 6)))
        .catch(() => {});
    }
  }, [token, API, projects]);

  const handleStyleSelect = (pad) => {
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current.currentTime = 0;
      setPreviewPlayingPad(null);
    }
    setSelectedPad(pad.id);
    // Pass both the message and the style name for the creative title
    setTimeout(() => onCreateMusic(pad.msg, pad.label), 350);
  };

  const togglePreview = async (e, styleId) => {
    e.stopPropagation();
    const previewUrl = STYLE_PREVIEWS?.[styleId]?.previewUrl;
    if (!previewUrl) return;

    if (!previewAudioRef.current) {
      previewAudioRef.current = new Audio();
      previewAudioRef.current.onended = () => setPreviewPlayingPad(null);
    }
    const audio = previewAudioRef.current;

    if (previewPlayingPad === styleId) {
      audio.pause();
      audio.currentTime = 0;
      setPreviewPlayingPad(null);
      return;
    }

    audio.pause();
    audio.currentTime = 0;
    audio.src = previewUrl;
    try {
      await audio.play();
      setPreviewPlayingPad(styleId);
    } catch {
      setPreviewPlayingPad(null);
    }
  };

  useEffect(() => {
    return () => {
      if (previewAudioRef.current) {
        previewAudioRef.current.pause();
        previewAudioRef.current = null;
      }
    };
  }, []);

  // ── Style Picker ──
  if (view === 'styles') {
    return (
      <div
        className="fixed inset-0 bg-[#0A0A0A] flex flex-col items-center justify-center px-4 sm:px-6 producer-noise z-[60]"
        data-testid="producer-style-picker"
      >
        <button
          onClick={() => { setView('main'); setSelectedPad(null); }}
          className="absolute top-4 sm:top-6 left-4 sm:left-6 flex items-center gap-1 sm:gap-1.5 text-[#52525B] hover:text-white transition-colors z-10"
          data-testid="style-picker-back"
        >
          <ChevronLeft size={14} className="sm:w-4 sm:h-4" />
          <span className="text-[10px] sm:text-[11px] font-medium">Atrás</span>
        </button>

        <p className="text-[9px] sm:text-[10px] text-[#3F3F46] uppercase tracking-[0.3em] sm:tracking-[0.35em] mb-6 sm:mb-8 font-medium relative z-10">
          Elige tu estilo
        </p>

        <div className="grid grid-cols-3 gap-2 sm:gap-3 w-full max-w-[280px] sm:max-w-[340px] relative z-10">
          {STYLE_PADS.map((pad) => (
            <button
              key={pad.id}
              data-testid={`style-pad-${pad.id}`}
              onClick={() => handleStyleSelect(pad)}
              onMouseEnter={() => setHoveredPad(pad.id)}
              onMouseLeave={() => setHoveredPad(null)}
              disabled={selectedPad !== null}
              className="producer-pad aspect-square rounded-xl sm:rounded-2xl flex flex-col items-center justify-center gap-1 sm:gap-2 border-2 relative overflow-hidden"
              style={{
                background: selectedPad === pad.id
                  ? `${pad.color}30`
                  : `${pad.color}0A`,
                borderColor: selectedPad === pad.id
                  ? pad.color
                  : hoveredPad === pad.id
                    ? `${pad.color}80`
                    : `${pad.color}30`,
                boxShadow: selectedPad === pad.id
                  ? `0 0 40px ${pad.color}50, inset 0 0 30px ${pad.color}20`
                  : hoveredPad === pad.id
                    ? `0 0 20px ${pad.color}25`
                    : 'none',
              }}
            >
              {/* Hover preview overlay */}
              {hoveredPad === pad.id && !selectedPad && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/60 rounded-xl sm:rounded-2xl z-10 producer-fade-in">
                  <StyleWaveform styleId={pad.id} color={pad.color} width={60} height={18} />
                  <p className="text-[7px] sm:text-[8px] text-white/80 mt-1 px-1 text-center leading-tight">{pad.desc}</p>
                  <p className="text-[6px] sm:text-[7px] text-white/50 mt-0.5">{pad.bpm}</p>
                  <button
                    onClick={(e) => togglePreview(e, pad.id)}
                    className="mt-1 w-6 h-6 rounded-full bg-white/20 flex items-center justify-center pointer-events-auto hover:bg-white/30 transition-colors"
                    title="Escuchar preview"
                  >
                    {previewPlayingPad === pad.id ? <Pause size={10} /> : <Play size={10} className="ml-0.5" />}
                  </button>
                </div>
              )}
              {/* Color glow dot */}
              <div
                className="w-2 sm:w-3 h-2 sm:h-3 rounded-full mb-0.5 sm:mb-1 transition-all duration-300"
                style={{
                  background: pad.color,
                  boxShadow: `0 0 12px ${pad.color}80`,
                  opacity: selectedPad === pad.id ? 1 : 0.6,
                }}
              />
              <span
                className="text-[8px] sm:text-[10px] font-bold tracking-[0.15em] sm:tracking-[0.2em] transition-colors"
                style={{ color: selectedPad === pad.id ? pad.color : `${pad.color}CC` }}
              >
                {pad.label}
              </span>
            </button>
          ))}
        </div>

        {selectedPad && (
          <div className="mt-6 sm:mt-8 flex items-center gap-2 text-[#F5C842] relative z-10 producer-fade-in">
            <div className="w-3 sm:w-4 h-3 sm:h-4 border-2 border-[#F5C842] border-t-transparent rounded-full animate-spin" />
            <span className="text-[10px] sm:text-xs font-medium">Preparando tu sesión...</span>
          </div>
        )}
      </div>
    );
  }

  // ── Main Entry ──
  return (
    <div
      className="fixed inset-0 bg-[#0A0A0A] flex flex-col items-center justify-center px-4 sm:px-6 relative producer-noise z-[60]"
      data-testid="producer-entry-screen"
    >
      {/* Ambient glow */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[300px] sm:w-[500px] h-[300px] sm:h-[500px] rounded-full producer-breathe"
          style={{ background: 'radial-gradient(circle, #F5C842, transparent 70%)' }}
        />
      </div>

      {/* Logo */}
      <div className="mb-10 sm:mb-14 flex flex-col items-center gap-3 sm:gap-4 relative z-10 producer-fade-in">
        <div
          className="w-12 sm:w-16 h-12 sm:h-16 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg"
          style={{ background: 'linear-gradient(135deg, #F5C842, #FF6B00)' }}
        >
          <Music2 size={22} className="sm:w-7 sm:h-7 text-black" />
        </div>
        <div className="text-center">
          <h1 className="text-sm sm:text-base font-bold text-white tracking-[0.15em] sm:tracking-[0.2em] uppercase">
            Pulse Producer
          </h1>
        </div>
      </div>

      {/* Two hardware pads */}
      <div className="flex flex-col gap-3 sm:gap-4 w-full max-w-[280px] sm:max-w-xs relative z-10">
        {/* Create Music Pad */}
        <button
          data-testid="entry-create-music"
          onClick={() => setView('styles')}
          className="producer-pad group relative w-full rounded-xl sm:rounded-2xl p-4 sm:p-5 border-2 text-left"
          style={{
            background: 'linear-gradient(180deg, rgba(245,200,66,0.06) 0%, rgba(10,10,10,0) 100%)',
            borderColor: 'rgba(245,200,66,0.25)',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = 'rgba(245,200,66,0.7)';
            e.currentTarget.style.boxShadow = '0 0 40px rgba(245,200,66,0.15), inset 0 1px 0 rgba(245,200,66,0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = 'rgba(245,200,66,0.25)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-10 sm:w-12 h-10 sm:h-12 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0 bg-[#F5C84218]">
              <Music2 size={18} className="sm:w-[22px] sm:h-[22px] text-[#F5C842]" />
            </div>
            <div>
              <p className="text-xs sm:text-sm font-bold text-white tracking-wide">Crear música</p>
              <p className="text-[9px] sm:text-[10px] text-[#52525B] mt-0.5">Empieza desde cero</p>
            </div>
          </div>
          <ChevronRight
            size={14}
            className="sm:w-4 sm:h-4 absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 text-[#3F3F46] group-hover:text-[#F5C842] transition-colors"
          />
        </button>

        {/* Improve Music Pad */}
        <button
          data-testid="entry-improve-music"
          onClick={onImproveMusic}
          className="producer-pad group relative w-full rounded-xl sm:rounded-2xl p-4 sm:p-5 border-2 text-left"
          style={{
            background: 'linear-gradient(180deg, rgba(0,212,255,0.06) 0%, rgba(10,10,10,0) 100%)',
            borderColor: 'rgba(0,212,255,0.25)',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = 'rgba(0,212,255,0.7)';
            e.currentTarget.style.boxShadow = '0 0 40px rgba(0,212,255,0.15), inset 0 1px 0 rgba(0,212,255,0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = 'rgba(0,212,255,0.25)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-10 sm:w-12 h-10 sm:h-12 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0 bg-[#00D4FF18]">
              <Headphones size={18} className="sm:w-[22px] sm:h-[22px] text-[#00D4FF]" />
            </div>
            <div>
              <p className="text-xs sm:text-sm font-bold text-white tracking-wide">Mejorar tu canción</p>
              <p className="text-[9px] sm:text-[10px] text-[#52525B] mt-0.5">Sube y transforma</p>
            </div>
          </div>
          <ChevronRight
            size={14}
            className="sm:w-4 sm:h-4 absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 text-[#3F3F46] group-hover:text-[#00D4FF] transition-colors"
          />
        </button>
      </div>

      {/* Recent Projects */}
      {sessions.length > 0 && (
        <div className="mt-4 sm:mt-6 w-full max-w-[280px] sm:max-w-xs relative z-10">
          <p className="text-[8px] sm:text-[9px] text-[#3F3F46] uppercase tracking-[0.2em] mb-2 font-medium flex items-center gap-1.5">
            <Clock size={9} />
            Tus proyectos
          </p>
          {sessions.map(s => {
            const d = s.display || {};
            const title = d.title || s.project?.title || s.project?.base?.title || 'Sin titulo';
            const style = d.style || s.project?.base?.style || '';
            const status = d.status || (s.stages?.base_done ? 'produced' : 'chatting');
            const statusLabel = status === 'published' ? 'Publicado' : status === 'produced' ? 'Producido' : 'En chat';
            const statusColor = status === 'published' ? '#00D4FF' : status === 'produced' ? '#F5C842' : '#52525B';
            const lastMsg = d.last_message || '';
            return (
              <button key={s.session_id} onClick={() => onResumeSession?.(s)}
                data-testid={`resume-session-${s.session_id}`}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg border border-[#1E1E23] bg-[#0F0F12] hover:border-[#F5C84250] hover:bg-[#F5C84208] transition-all mb-1.5 group">
                <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: statusColor }} />
                <div className="flex-1 text-left min-w-0">
                  <p className="text-[9px] sm:text-[10px] font-medium text-white truncate">{title || lastMsg || 'Nuevo proyecto'}</p>
                  <p className="text-[7px] sm:text-[8px] text-[#52525B]">{style ? `${style} · ` : ''}{statusLabel}</p>
                </div>
                <ChevronRight size={10} className="text-[#3F3F46] group-hover:text-[#F5C842] transition-colors flex-shrink-0" />
              </button>
            );
          })}
        </div>
      )}

      {/* Removed wallet badge — no balance visible during creation */}
    </div>
  );
}
